import { Trophy, Crosshair, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Player } from '@/types/game';
import { getTeamColors, sanitizePlayerUrl } from '@/utils/game-utils';

interface PlayerCardProps {
  player: Player;
  rank: number;
  index: number;
}

const getRankIcon = (rank: number) => {
  switch (rank) {
    case 1:
      return <Trophy className="w-6 h-6 text-neon-yellow" />;
    case 2:
      return <Trophy className="w-5 h-5 text-gray-400" />;
    case 3:
      return <Trophy className="w-5 h-5 text-orange-400" />;
    default:
      return <span className="w-6 h-6 flex items-center justify-center text-lg font-bold text-muted-foreground">#{rank}</span>;
  }
};

export const PlayerCard = ({ player, rank, index }: PlayerCardProps) => {
  const colors = getTeamColors(player.team);
  const safeUrl = sanitizePlayerUrl(player.playerurl);

  const cardContent = (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
      <div className="flex items-center space-x-2 sm:space-x-4 w-full sm:w-auto">
        <div className="flex-shrink-0">
          {getRankIcon(rank)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`text-base sm:text-xl font-bold ${colors.text} truncate`}>
              {player.name}
            </h3>
            {safeUrl && (
              <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            )}
          </div>
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-muted-foreground">
            <Badge
              variant="outline"
              className={`${colors.border} ${colors.text} border-current text-xs`}
            >
              Team {player.team}
            </Badge>
            <span className="text-xs">Weste #{player.weste}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-3 sm:space-x-6 text-right w-full sm:w-auto justify-between sm:justify-end">
        <div className="space-y-1">
          <div className={`text-xl sm:text-2xl font-bold ${colors.text}`}>
            {parseInt(player.score).toLocaleString()}
          </div>
          <div className="text-xs text-muted-foreground">Punkte</div>
        </div>
        <div className="space-y-1">
          <div className="flex items-center space-x-1 text-xs sm:text-sm">
            <Crosshair className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{player.shots}</span>
          </div>
          <div className="text-xs text-muted-foreground">Schüsse</div>
        </div>
        <div className="space-y-1">
          <div className="text-xs sm:text-sm font-medium">
            {player.accurancy}%
          </div>
          <div className="text-xs text-muted-foreground">Quote</div>
        </div>
      </div>
    </div>
  );

  const cardClassName = `p-3 sm:p-4 border ${colors.border} ${colors.bg} backdrop-blur-sm transition-all duration-300 hover:${colors.glow} hover:scale-[1.01] sm:hover:scale-[1.02] animate-fade-in`;

  return safeUrl ? (
    <a
      href={safeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block"
    >
      <Card
        className={`${cardClassName} cursor-pointer`}
        style={{ animationDelay: `${index * 0.1}s` }}
      >
        {cardContent}
      </Card>
    </a>
  ) : (
    <Card
      className={cardClassName}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      {cardContent}
    </Card>
  );
};
